import {Helmet} from 'react-helmet'
import {TKey, useTranslation} from '@/translations/i18n.tsx'
import {z} from 'zod'
import {zodResolver} from '@hookform/resolvers/zod'
import {useForm} from 'react-hook-form'
import {
    AuthFormCard,
    AuthFormCardSubtitle,
    AuthFormCardTitle,
    AuthPageContainer
} from '@features/user/components/auth-form-atoms'
import {InputText} from '@components/commons/input-text'
import {Button} from '@components/ui/button'
import {Spinner} from '@components/ui/spinner'
import {ButtonLink} from '@components/commons/button-link'
import {routes} from '@utilities/constants/routes.tsx'
import {useAsync} from '@hooks/useAsync.ts'
import {httpCheckResetPasswordToken, httpResetPassword} from '@features/user/http/user.http.ts'
import {errorHandler, raise} from '@utilities/helpers.ts'
import {useNavigate, useParams} from 'react-router-dom'
import {SuccessModal} from '@components/commons/success-modal'
import {useEffect, useState} from 'react'
import {ErrorBox} from '@components/commons/error-box'
import {AlertTriangleIcon} from '@components/ui/icon'

const ResetPasswordValidationSchema = z
    .object({
        password: z.string().min(1, {message: 'errors:required'}).min(8, {message: 'errors:password_invalid_length'}),
        confirmedNewPassword: z
            .string()
            .min(1, {message: 'errors:required'})
            .min(8, {message: 'errors:password_invalid_length'})
    })
    .refine(values => values.password == values.confirmedNewPassword, {
        message: 'commons:confirm_password_error',
        path: ['confirmedNewPassword']
    })
export type ResetPasswordValidationSchema = z.infer<typeof ResetPasswordValidationSchema>

export const ResetPassword = () => {
    const {t} = useTranslation()
    const params = useParams()
    const token = params.token ?? raise('token is nullish')
    const form = useForm<ResetPasswordValidationSchema>({resolver: zodResolver(ResetPasswordValidationSchema)})
    const resetPassword = useAsync()
    const checkToken = useAsync()
    const navigate = useNavigate()
    const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false)

    useEffect(() => {
        void verifyToken()
    }, [])

    const handleSubmit = form.handleSubmit(async formValues => {
        try {
            await resetPassword.run(
                httpResetPassword({
                    payload: {password: formValues.password},
                    urlParams: {token}
                })
            )
            setIsSuccessModalOpen(true)
        } catch (error) {
            errorHandler(error)
        }
    })
    const verifyToken = async () => {
        await checkToken.run(httpCheckResetPasswordToken({urlParams: {token}}))
    }

    return (
        <AuthPageContainer>
            <Helmet title={t('titles:reset_password')} />
            {isSuccessModalOpen && (
                <SuccessModal
                    paragraph={t('commons:password_updated')}
                    buttonLabel={t('auth:login')}
                    onClickCta={() => navigate(routes.LOGIN.handle.fullPath, {replace: true})}
                />
            )}

            {checkToken.isIdle || checkToken.isLoading ? (
                <Spinner size={48} />
            ) : checkToken.isError ? (
                <ErrorBox icon={<AlertTriangleIcon />} title={t('commons:invalid_reset_link')} />
            ) : (
                <AuthFormCard onSubmit={handleSubmit}>
                    <div className="flex flex-col items-center gap-6">
                        <AuthFormCardTitle>{t('auth:reset_password')}</AuthFormCardTitle>
                        <AuthFormCardSubtitle>{t('auth:enter_password')}</AuthFormCardSubtitle>
                    </div>
                    <div className="flex flex-col gap-6">
                        <InputText
                            type="password"
                            className="w-full"
                            visibilityToggle
                            label={t('commons:new_password')}
                            errorMessage={t((form.formState.errors.password?.message ?? '') as TKey)}
                            {...form.register('password')}
                        />
                        <InputText
                            type="password"
                            className="w-full"
                            visibilityToggle
                            label={t('commons:confirm_password')}
                            errorMessage={t((form.formState.errors.confirmedNewPassword?.message ?? '') as TKey)}
                            {...form.register('confirmedNewPassword')}
                        />
                    </div>
                    <div className="flex flex-col items-center gap-6">
                        <Button size="lg" disabled={resetPassword.isLoading} className="w-full" type="submit">
                            {t('commons:submit')}
                            {resetPassword.isLoading && <Spinner />}
                        </Button>
                        <ButtonLink
                            size="xs"
                            to={routes.LOGIN.handle.fullPath}
                            variant="tertiary"
                            disabled={resetPassword.isLoading}
                        >
                            {t('auth:login')}
                        </ButtonLink>
                    </div>
                </AuthFormCard>
            )}
        </AuthPageContainer>
    )
}
